import React from 'react';
import { Star, User, Copy } from 'lucide-react';
import { formatNumber } from '../utils/formatUtils';
import { formatTimeAgo } from '../utils/dateUtils';

const RepoCard = ({ repo, innerRef }) => {
  return (
    <div
      ref={innerRef}
      className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200 dark:bg-gray-800 dark:border-gray-700 dark:hover:shadow-lg"
    >
      <div className="p-5">
        {/* Repo Header */}
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="flex-1 min-w-0">
            <a
              href={repo.html_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xl font-semibold text-blue-600 hover:text-blue-800 dark:text-blue-300 dark:hover:text-blue-200 hover:underline inline-block break-words"
            >
              {repo.name}
            </a>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(repo.html_url);
                } catch (e) {
                  // fallback: create temporary input
                  const temp = document.createElement('input');
                  temp.value = repo.html_url;
                  document.body.appendChild(temp);
                  temp.select();
                  document.execCommand('copy');
                  document.body.removeChild(temp);
                }
              }}
              aria-label="Copy repository link"
              className="p-1 rounded-md hover:bg-gray-100"
              title="Copy link"
            >
              <Copy size={16} className="text-gray-500" />
            </button>

            <div className="flex items-center gap-1.5 bg-yellow-50 px-3 py-1.5 rounded-full">
            <Star className="text-yellow-500 fill-yellow-500" size={18} />
            <span className="font-semibold text-gray-900">
              {formatNumber(repo.stargazers_count)}
            </span>
            </div>
          </div>
        </div>

        {/* Description */}
        <p className="text-gray-700 dark:text-gray-300 mb-3 leading-relaxed">
          {repo.description || 'No description provided.'}
        </p>

        {/* Meta: language and time ago */}
        <div className="flex items-center gap-3 mb-4 text-sm text-gray-500 dark:text-gray-300">
          {repo.language && (
            <span className="px-2 py-0.5 bg-gray-100 rounded-full text-gray-700 dark:bg-gray-700 dark:text-gray-200">{repo.language}</span>
          )}
          {repo.created_at && (
            <span className="text-gray-500 dark:text-gray-300">Created {formatTimeAgo(repo.created_at)} ago</span>
          )}
        </div>

        {/* Owner Info */}
        <div className="flex items-center gap-3 pt-3 border-t border-gray-100">
          <a
            href={repo.owner.html_url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 hover:opacity-80 transition-opacity group"
          >
            <img
              src={repo.owner.avatar_url}
              alt={repo.owner.login}
              loading="lazy"
              onError={(e) => {
                e.currentTarget.onerror = null;
                const initials = (repo.owner.login || '').slice(0, 2).toUpperCase();
                const svg = encodeURIComponent(`<svg xmlns='http://www.w3.org/2000/svg' width='40' height='40'><rect width='100%' height='100%' fill='%23E5E7EB'/><text x='50%' y='50%' alignment-baseline='middle' text-anchor='middle' font-size='14' fill='%239CA3AF'>${initials}</text></svg>`);
                e.currentTarget.src = `data:image/svg+xml;utf8,${svg}`;
              }}
              className="w-10 h-10 rounded-full border-2 border-gray-200 group-hover:border-blue-400 transition-colors dark:border-gray-600"
            />
            <div className="flex items-center gap-2">
              <User size={16} className="text-gray-400" />
              <span className="font-medium text-gray-900 group-hover:text-blue-600 dark:text-gray-100 group-hover:dark:text-blue-200">
                {repo.owner.login}
              </span>
            </div>
          </a>
        </div>
      </div>
    </div>
  );
};

export default RepoCard;